import React, { useState } from "react";
import {
  TickCircle,
  Magicpen,
  Refresh2,
  Box,
  Tag2,
  CloseCircle,
  SearchNormal1,
} from "iconsax-react";

const rawApiUrl = process.env.REACT_APP_API_URL || "/api";
const API_BASE =
  typeof window !== "undefined" &&
  window.location.protocol === "https:" &&
  rawApiUrl.startsWith("http://")
    ? rawApiUrl.replace("http://", "https://")
    : rawApiUrl;

const PINK = "#e91e8c";
const PINK_LIGHT = "#f472b6";
const PINK_BG = "#fdf2f8";

const STATUS_LABEL = {
  diterima: "Diterima",
  dicuci: "Sedang Dicuci",
  disetrika: "Sedang Disetrika",
  selesai_setrika: "Selesai Setrika",
  siap_ambil: "Siap Diambil",
  diambil: "Sudah Diambil",
  cancelled: "Dibatalkan",
};

const STATUS_COLOR = {
  diterima: "#f59e0b",
  dicuci: "#06b6d4",
  disetrika: "#6366f1",
  selesai_setrika: "#475569",
  siap_ambil: "#10b981",
  diambil: "#94a3b8",
  cancelled: "#ef4444",
};

const STATUS_BG = {
  diterima: "#fffbeb",
  dicuci: "#ecfeff",
  disetrika: "#eef2ff",
  selesai_setrika: "#f8fafc",
  siap_ambil: "#ecfdf5",
  diambil: "#f8fafc",
  cancelled: "#fef2f2",
};

const STATUS_STEPS = [
  "diterima",
  "dicuci",
  "disetrika",
  "selesai_setrika",
  "siap_ambil",
  "diambil",
];

const DOT_ICON = (status, color) => {
  const props = { size: 16, color, variant: "Bold" };
  switch (status) {
    case "diterima": return <Tag2 {...props} />;
    case "dicuci": return <Refresh2 {...props} />;
    case "disetrika": return <Magicpen {...props} />;
    case "selesai_setrika": return <TickCircle {...props} />;
    case "siap_ambil": return <Box {...props} />;
    case "diambil": return <TickCircle {...props} />;
    case "cancelled": return <CloseCircle {...props} />;
    default: return <TickCircle {...props} />;
  }
};

const fmtDt = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  const date = dt.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const h = String(dt.getHours()).padStart(2, "0");
  const m = String(dt.getMinutes()).padStart(2, "0");
  return `${date}, ${h}:${m}`;
};

const TrackOrder = () => {
  const [invoice, setInvoice] = useState("");
  const [phone, setPhone] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = invoice.trim() && phone.trim();

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await fetch(
        `${API_BASE}/transactions/track/${encodeURIComponent(invoice.trim())}?phone=${encodeURIComponent(phone.trim())}`,
      );
      if (res.status === 404) {
        setError("Nomor invoice tidak ditemukan. Periksa kembali nomor invoice Anda.");
        return;
      }
      if (res.status === 403) {
        setError("Nomor HP tidak sesuai dengan data transaksi ini.");
        return;
      }
      if (res.status === 400) {
        setError("Nomor HP wajib diisi.");
        return;
      }
      if (!res.ok) {
        setError("Terjadi kesalahan. Silakan coba lagi.");
        return;
      }
      const data = await res.json();
      setResult(data);
    } catch {
      setError("Tidak dapat terhubung ke server. Periksa koneksi internet Anda.");
    } finally {
      setLoading(false);
    }
  };

  const statusColor = result ? STATUS_COLOR[result.status] || "#94a3b8" : "";
  const statusBg = result ? STATUS_BG[result.status] || "#f8fafc" : "";
  const statusLabel = result ? STATUS_LABEL[result.status] || result.status : "";

  const services = result?.details?.filter((d) => d.item_type === "service") || [];
  const products = result?.details?.filter((d) => d.item_type === "product") || [];

  /* progress stepper: build steps dynamically based on actual flow */
  const isCancelled = result?.status === "cancelled";

  const hasIroning =
    result?.status === "disetrika" ||
    result?.status === "selesai_setrika" ||
    (result?.logs || []).some(
      (l) => l.to_status === "disetrika" || l.to_status === "selesai_setrika",
    );

  const activeSteps = hasIroning
    ? ["diterima", "dicuci", "disetrika", "selesai_setrika", "siap_ambil", "diambil"]
    : ["diterima", "dicuci", "siap_ambil", "diambil"];

  const currentStepIndex = isCancelled
    ? -1
    : activeSteps.indexOf(result?.status ?? "");

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(160deg, #fdf2f8 0%, #fce7f3 55%, #fbcfe8 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "0 0 60px",
      }}
    >
      {/* â”€â”€ Top header strip â”€â”€ */}
      <div
        style={{
          width: "100%",
          background: `linear-gradient(135deg, ${PINK}, ${PINK_LIGHT})`,
          padding: "28px 20px 56px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* decorative circles */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute", top: -40, right: -40,
            width: 160, height: 160, borderRadius: "50%",
            background: "rgba(255,255,255,0.1)", pointerEvents: "none",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute", bottom: -30, left: -30,
            width: 120, height: 120, borderRadius: "50%",
            background: "rgba(255,255,255,0.08)", pointerEvents: "none",
          }}
        />

        <div
          style={{
            width: 56, height: 56, borderRadius: 16,
            background: "rgba(255,255,255,0.2)",
            backdropFilter: "blur(8px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 12px",
          }}
        >
          <SearchNormal1 size={26} color="#fff" variant="Bold" />
        </div>
        <h5
          className="fw-bold mb-1"
          style={{ color: "#fff", letterSpacing: "-0.01em" }}
        >
          Cek Status Laundry PINK99
        </h5>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.85rem", margin: 0 }}>
          Lacak cucian Anda kapan saja tanpa login
        </p>
      </div>

      {/* â”€â”€ Search card (overlapping header) â”€â”€ */}
      <div
        style={{
          width: "100%",
          maxWidth: 520,
          padding: "0 16px",
          marginTop: -28,
          position: "relative",
          zIndex: 10,
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 20,
            boxShadow: "0 8px 32px rgba(233,30,140,0.14), 0 2px 8px rgba(0,0,0,0.06)",
            padding: "24px 20px 20px",
          }}
        >
          <form onSubmit={handleTrack}>
            <label
              style={{
                display: "block",
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "#94a3b8",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                marginBottom: 8,
              }}
            >
              Nomor Invoice
            </label>
            <input
              type="text"
              value={invoice}
              onChange={(e) => setInvoice(e.target.value)}
              placeholder="Contoh: INV-20240101-001"
              autoFocus
              style={{
                width: "100%",
                border: `2px solid ${invoice ? PINK : "#e2e8f0"}`,
                borderRadius: 12,
                padding: "11px 14px",
                fontSize: "0.95rem",
                outline: "none",
                transition: "border-color 0.2s",
                color: "#1e293b",
                boxSizing: "border-box",
                marginBottom: 12,
              }}
              onFocus={(e) => (e.target.style.borderColor = PINK)}
              onBlur={(e) =>
                (e.target.style.borderColor = invoice ? PINK : "#e2e8f0")
              }
            />

            <label
              style={{
                display: "block",
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "#94a3b8",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                marginBottom: 8,
              }}
            >
              Nomor HP
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Contoh: 08123456789"
              style={{
                width: "100%",
                border: `2px solid ${phone ? PINK : "#e2e8f0"}`,
                borderRadius: 12,
                padding: "11px 14px",
                fontSize: "0.95rem",
                outline: "none",
                transition: "border-color 0.2s",
                color: "#1e293b",
                boxSizing: "border-box",
                marginBottom: 12,
              }}
              onFocus={(e) => (e.target.style.borderColor = PINK)}
              onBlur={(e) =>
                (e.target.style.borderColor = phone ? PINK : "#e2e8f0")
              }
            />
            <button
              type="submit"
              disabled={loading || !canSubmit}
              style={{
                width: "100%",
                padding: "13px 20px",
                borderRadius: 12,
                border: "none",
                background:
                  loading || !canSubmit
                    ? "#f9a8d4"
                    : `linear-gradient(135deg, ${PINK}, ${PINK_LIGHT})`,
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.95rem",
                cursor: loading || !canSubmit ? "not-allowed" : "pointer",
                boxShadow:
                  loading || !canSubmit
                    ? "none"
                    : "0 4px 14px rgba(233,30,140,0.4)",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              {loading ? (
                <span className="spinner-border spinner-border-sm" role="status" />
              ) : (
                <>
                  <SearchNormal1 size={16} color="#fff" />
                  Cek Status
                </>
              )}
            </button>
          </form>

          {error && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: "#fef2f2",
                border: "1.5px solid #fca5a5",
                borderRadius: 10,
                fontSize: "0.85rem",
                color: "#dc2626",
              }}
            >
              {error}
            </div>
          )}

          <p
            style={{
              marginTop: 12,
              marginBottom: 0,
              fontSize: "0.75rem",
              color: "#94a3b8",
              textAlign: "center",
            }}
          >
            Nomor invoice &amp; HP tertera pada struk yang Anda terima
          </p>
        </div>

        {/* â”€â”€ Result â”€â”€ */}
        {result && (
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              boxShadow: "0 8px 32px rgba(233,30,140,0.10), 0 2px 8px rgba(0,0,0,0.05)",
              marginTop: 16,
              overflow: "hidden",
            }}
          >
            {/* Status banner */}
            <div
              style={{
                background: isCancelled
                  ? "#fef2f2"
                  : `linear-gradient(135deg, ${PINK}18, ${PINK_LIGHT}18)`,
                borderBottom: `1px solid ${isCancelled ? "#fca5a5" : PINK + "25"}`,
                padding: "18px 20px",
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  background: isCancelled ? "#fee2e2" : `${PINK}18`,
                  border: `2px solid ${isCancelled ? "#ef4444" : PINK}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {DOT_ICON(result.status, isCancelled ? "#ef4444" : PINK)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: "1rem",
                    fontWeight: 700,
                    color: isCancelled ? "#ef4444" : PINK,
                  }}
                >
                  {statusLabel}
                </div>
                <div style={{ fontSize: "0.8rem", color: "#94a3b8", marginTop: 2 }}>
                  {result.invoice_number}
                </div>
              </div>
              <span
                style={{
                  padding: "4px 12px",
                  borderRadius: 20,
                  background: isCancelled ? "#ef4444" : PINK,
                  color: "#fff",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {isCancelled ? "Batal" : result.status === "diambil" ? "Selesai" : "Proses"}
              </span>
            </div>

            {/* Progress stepper (hidden if cancelled) */}
            {!isCancelled && (
              <div style={{ padding: "16px 20px 4px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    overflowX: "auto",
                    paddingBottom: 4,
                    gap: 0,
                  }}
                >
                  {activeSteps.map((step, idx) => {
                    const done = idx <= currentStepIndex;
                    const active = idx === currentStepIndex;
                    const color = done ? PINK : "#e2e8f0";
                    return (
                      <React.Fragment key={step}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flexShrink: 0,
                            minWidth: 48,
                          }}
                        >
                          <div
                            style={{
                              width: active ? 28 : 22,
                              height: active ? 28 : 22,
                              borderRadius: "50%",
                              background: done ? PINK : "#f1f5f9",
                              border: `2px solid ${color}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.3s",
                              boxShadow: active
                                ? `0 0 0 4px ${PINK}22`
                                : "none",
                            }}
                          >
                            {done && (
                              <TickCircle
                                size={active ? 16 : 12}
                                color="#fff"
                                variant="Bold"
                              />
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: "0.6rem",
                              marginTop: 4,
                              color: done ? PINK : "#94a3b8",
                              fontWeight: done ? 700 : 400,
                              textAlign: "center",
                              lineHeight: 1.2,
                              maxWidth: 48,
                            }}
                          >
                            {STATUS_LABEL[step]}
                          </div>
                        </div>
                        {idx < activeSteps.length - 1 && (
                          <div
                            style={{
                              flex: 1,
                              height: 2,
                              minWidth: 8,
                              background:
                                idx < currentStepIndex
                                  ? `linear-gradient(90deg, ${PINK}, ${PINK_LIGHT})`
                                  : "#e2e8f0",
                              marginBottom: 16,
                              transition: "background 0.3s",
                            }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Info rows */}
            <div style={{ padding: "8px 20px 4px" }}>
              {[
                { label: "Pelanggan", value: result.customer_name || "-" },
                { label: "Tanggal Masuk", value: fmtDt(result.created_at) },
                result.weight_kg && {
                  label: "Berat",
                  value: `${result.weight_kg} kg`,
                },
                result.total_pcs && {
                  label: "Jumlah",
                  value: `${result.total_pcs} pcs`,
                },
              ]
                .filter(Boolean)
                .map((row, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "8px 0",
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: "0.875rem",
                    }}
                  >
                    <span style={{ color: "#94a3b8" }}>{row.label}</span>
                    <span style={{ fontWeight: 600, color: "#1e293b", textAlign: "right", maxWidth: "60%" }}>
                      {row.value}
                    </span>
                  </div>
                ))}
            </div>

            {/* Items */}
            {(services.length > 0 || products.length > 0) && (
              <div style={{ padding: "12px 20px" }}>
                <div
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    marginBottom: 8,
                  }}
                >
                  Item Laundry
                </div>
                {[...services, ...products].map((d, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "7px 12px",
                      marginBottom: 6,
                      background: PINK_BG,
                      borderRadius: 10,
                      fontSize: "0.875rem",
                    }}
                  >
                    <span style={{ color: "#1e293b" }}>{d.item_name}</span>
                    <span
                      style={{
                        background: PINK,
                        color: "#fff",
                        borderRadius: 20,
                        padding: "2px 10px",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                      }}
                    >
                      &times; {parseInt(d.quantity)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Timeline */}
            {((result.logs || []).length > 0 || result.created_at) && (
              <div style={{ padding: "4px 20px 20px" }}>
                <div
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    marginBottom: 14,
                  }}
                >
                  Riwayat Status
                </div>
                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      position: "absolute",
                      left: 15,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      background: `linear-gradient(180deg, ${PINK}50, ${PINK_LIGHT}20)`,
                    }}
                  />

                  {/* Diterima entry */}
                  {(() => {
                    const dt = new Date(result.created_at);
                    const timeStr =
                      String(dt.getHours()).padStart(2, "0") +
                      ":" +
                      String(dt.getMinutes()).padStart(2, "0");
                    const dateStr = dt.toLocaleDateString("id-ID", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    });
                    return (
                      <div
                        style={{
                          display: "flex",
                          gap: 12,
                          marginBottom: 16,
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: `${STATUS_COLOR.diterima}22`,
                            border: `2px solid ${STATUS_COLOR.diterima}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            zIndex: 1,
                          }}
                        >
                          {DOT_ICON("diterima", STATUS_COLOR.diterima)}
                        </div>
                        <div style={{ paddingTop: 4, flex: 1 }}>
                          <div
                            style={{
                              fontSize: "0.85rem",
                              fontWeight: 700,
                              color: STATUS_COLOR.diterima,
                            }}
                          >
                            Diterima
                          </div>
                          <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: 2 }}>
                            {dateStr} Â· {timeStr}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {(result.logs || []).map((log, i) => {
                    const color = STATUS_COLOR[log.to_status] || "#94a3b8";
                    const label = STATUS_LABEL[log.to_status] || log.to_status;
                    const dt = new Date(log.changed_at);
                    const timeStr =
                      String(dt.getHours()).padStart(2, "0") +
                      ":" +
                      String(dt.getMinutes()).padStart(2, "0");
                    const dateStr = dt.toLocaleDateString("id-ID", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    });
                    return (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          gap: 12,
                          marginBottom: 16,
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: color + "22",
                            border: `2px solid ${color}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            zIndex: 1,
                          }}
                        >
                          {DOT_ICON(log.to_status, color)}
                        </div>
                        <div style={{ paddingTop: 4, flex: 1 }}>
                          <div
                            style={{
                              fontSize: "0.85rem",
                              fontWeight: 700,
                              color,
                            }}
                          >
                            {label}
                          </div>
                          <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: 2 }}>
                            {dateStr} Â· {timeStr}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default TrackOrder;
